/**
 * Timing design tokens: time-based values (ms unless noted) for animations, transitions, notifications, loading states,
 * feedback, and page transitions.
 *
 * Homed in const/ beside the `motion` scale it derives from: these ARE decisions ("a toast shows for four seconds"),
 * and const/ is the one tier every consumer reaches -- the SDL primitives under `components/`, the websocket transport
 * under `components/lib/`, and any `utils/` leaf, which may value-import only const/types/zod.
 */

import { motion as canonicalMotion } from '@federa/const/interaction-data';

export const notifications = {
	toast: 4000,
	/** Shown longer so the user can read the error. */
	toastError: 6000,
	toastSuccess: 3000,
	toastWarning: 5000,
	toastInfo: 4000,
	/** Persistent until dismissed. */
	banner: -1,
	snackbar: 3000,
} as const;

/** Loading indicator timing chosen to prevent flicker. */
export const loading = {
	/** Delay before showing the spinner at all -- prevents flicker on fast loads. */
	spinnerDelay: 500,
	/** Min time to keep a shown spinner up -- prevents flicker. */
	minimumDisplay: 400,
	/** Request timeout: considered failed after this. */
	timeout: 30000,
	/** Prevents a jarring skeleton flash. */
	skeletonMinimum: 300,
	/** Show optimistic UI immediately, but wait this long before reverting. */
	optimisticDelay: 100,
	pollingInterval: 5000,
} as const;

export const feedback = {
	copySuccess: 2000,
	actionConfirm: 2000,
	validationMessage: 3000,
} as const;

/** Parse a canonical "Xms" CSS duration string into its numeric ms value. Named for the CSS string it reads, not
 *  `parseMs`: that name is the exported ISO-instant reader in `const/dev-stack-session-end-witnesses.ts`, a different
 *  input answering a different question. */
function cssDurationMs(ms: string): number {
	return Number.parseInt(ms, 10);
}

/** Speed keys present in both the motion scale and the derived numeric/string forms. */
const CANONICAL_SPEEDS = ['fastest', 'fast', 'medium', 'base', 'normal', 'slow', 'slower', 'slowest'] as const;

type CanonicalSpeed = (typeof CANONICAL_SPEEDS)[number];
type ExtendedSpeed = 'instant' | CanonicalSpeed | 'crawl';

// CANONICAL SOURCE for both derived maps: const/interaction-data.ts -> motion.duration
const numericDurations = Object.fromEntries([
	['instant', 0] as const,
	...CANONICAL_SPEEDS.map((k) => [k, cssDurationMs(canonicalMotion.duration[k])] as const),
	['crawl', 1500] as const,
]) as Record<ExtendedSpeed, number>;

const cssDurations = Object.fromEntries([
	['instant', '0ms'] as const,
	...CANONICAL_SPEEDS.map((k) => [k, canonicalMotion.duration[k]] as const),
	['crawl', '1500ms'] as const,
]) as Record<ExtendedSpeed, string>;

export const animations = {
	...numericDurations,

	/** String values for CSS usage. */
	duration: cssDurations,
	/** `standard` is the canonical brand easing; the rest are supplementary curves. Every CURVE here binds the
	 *  interaction-data scale -- only the two bare CSS keywords are written literally, because a keyword is a browser
	 *  primitive rather than a brand decision. */
	easing: {
		easeInOut: canonicalMotion.easing.standard,
		easeOut: 'ease-out',
		easeIn: 'ease-in',
		spring: canonicalMotion.easing.spring,
		standard: canonicalMotion.easing.standard,
	},
} as const;

/**
 * Named-intent transition durations over the SAME `motion.duration` scale the two derived maps above read -- the
 * "fold or derive" half of G11 / D7 (`docs/plans/design-language-p0-census.md`). Every member whose value EQUALS a
 * rung is now DERIVED from it and can no longer drift; the three that equal no rung stay literal and carry a ground.
 *
 * Mapping measured against `durationScale` (fastest 80 / fast 120 / medium 200 / base 250 / slow 320 / slower 500 /
 * slowest 800): DERIVED -- exit + modalClose = medium, modalOpen + accordion = base. OFF-SCALE -- enter 300,
 * drawerSlide 300, tabSwitch 150.
 *
 * The off-scale three are held at their literals because NO ground for them exists, not because one was found: the
 * whole record arrived intact in the bulk design-system import (`e7d35e7fd75`) carrying only name-restating
 * docstrings, and nothing in the tree argues 300 or 150. Snapping them to a rung would CHANGE the drawer's slide
 * timing -- a design call this derivation is not entitled to make. Pick a rung deliberately, or leave them.
 */
export const pageTransitions = {
	enter: 300,
	exit: numericDurations.medium,
	modalOpen: numericDurations.base,
	modalClose: numericDurations.medium,
	drawerSlide: 300,
	tabSwitch: 150,
	accordion: numericDurations.base,
} as const;
